import { HttpHeaders } from '@angular/common/http';

import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache, CombinedGraphQLErrors } from '@apollo/client/core';
import type { GraphQLFormattedError } from 'graphql';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';

import { IInfrastructureConfig } from '../../models';

/**
 * Notified about every GraphQL error response, so an app can surface it to the
 * user (see web-saas, which shows an error toast). Omit to only log.
 */
export type GraphQLErrorHandler = (errors: readonly GraphQLFormattedError[]) => void;

/**
 * Apollo operation context flag: set `context: { [SKIP_ERROR_HANDLER]: true }`
 * on a query/mutation whose errors must not reach `onGraphQLErrors` — e.g.
 * background calls the user never triggered and should not be told about.
 */
export const SKIP_ERROR_HANDLER = 'skipErrorHandler';

/**
 * Creates Apollo Client configuration for both web and NativeScript apps
 * Compatible with Apollo Client v4 and apollo-angular v12
 */
export function createDefaultApolloConnection(
  httpLink: HttpLink,
  config: IInfrastructureConfig,
  onGraphQLErrors?: GraphQLErrorHandler
) {
  // 1. Context Link: Add/modify headers for all requests
  const contextLink = new SetContextLink(prevContext => {
    // Type assertion: prevContext contains headers but Apollo's types don't expose it
    const existingHeaders = (prevContext as { headers?: HttpHeaders | Record<string, string> })
      .headers;

    // Handle both HttpHeaders (Angular) and plain objects
    const headers =
      existingHeaders instanceof HttpHeaders
        ? existingHeaders.set('Accept', 'application/json')
        : new HttpHeaders({
            ...(existingHeaders || {}),
            Accept: 'application/json',
          });

    return { headers };
  });

  // 2. Error Link: Handle GraphQL and network errors
  const errorLink = new ErrorLink(({ error, operation }) => {
    // Check for GraphQL errors
    if (CombinedGraphQLErrors.is(error)) {
      // Log GraphQL errors for debugging
      error.errors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      });
      const skipErrorHandler = (operation.getContext() as Record<string, unknown>)?.[
        SKIP_ERROR_HANDLER
      ];
      if (!skipErrorHandler) {
        onGraphQLErrors?.(error.errors);
      }
    } else {
      // Network or other errors
      console.error('[Network error]:', error);
    }
  });

  // 3. HTTP Link: Send requests to GraphQL server
  const http = httpLink.create({
    uri: config.environment.graphqlServerUri,
  });

  // 4. Compose link chain
  const link = ApolloLink.from([contextLink, errorLink, http]);

  // 5. Configure cache
  const cache = new InMemoryCache();

  // 6. Set default options
  const fetchPolicy = 'no-cache';
  const defaultOptions = {
    watchQuery: {
      fetchPolicy,
    },
    query: {
      fetchPolicy,
    },
  };

  // 7. Return Apollo configuration
  return {
    link,
    cache,
    defaultOptions,
  };
}
